import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { defineStore } from "pinia";

import {
  customerOrdersApi,
  type CustomerOrderDetail,
  type CustomerOrderSummary,
} from "../api/customerOrders";

export interface OrdersGroupedByDate {
  dateKey: string;
  formattedDate: string;
  orders: CustomerOrderSummary[];
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    const response = (err as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
    const message = (err as { message?: string }).message;
    if (message) {
      return message;
    }
  }

  return fallback;
}

export const useCustomerOrdersStore = defineStore("customerOrders", {
  state: () => ({
    orders: [] as CustomerOrderSummary[],
    currentOrder: null as CustomerOrderDetail | null,
    loading: false,
    detailLoading: false,
    actionLoading: false,
    error: null as string | null,
    pollTimer: null as ReturnType<typeof setInterval> | null,
    trackingPollTimer: null as ReturnType<typeof setInterval> | null,
    requestGeneration: 0,
  }),

  getters: {
    pendingPaymentOrders(state): CustomerOrderSummary[] {
      return state.orders.filter((o) => o.isPendingPayment);
    },

    inProgressOrders(state): CustomerOrderSummary[] {
      return state.orders.filter((o) => o.isInProgress);
    },

    finishedOrders(state): CustomerOrderSummary[] {
      return state.orders.filter((o) => o.isFinished);
    },

    hasActiveOrders(state): boolean {
      return state.orders.some((o) => o.isInProgress || o.isPendingPayment);
    },

    finishedOrdersGroupedByDate(state): OrdersGroupedByDate[] {
      const finished = state.orders.filter((o) => o.isFinished);
      const groups: Record<string, CustomerOrderSummary[]> = {};

      for (const order of finished) {
        const dateObj =
          typeof order.createdDate === "string"
            ? parseISO(order.createdDate)
            : new Date(order.createdDate);
        const dateKey = format(dateObj, "yyyy-MM-dd");

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(order);
      }

      return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map((dateKey) => {
          const [year, month, day] = dateKey.split("-").map(Number);
          const dateObj = new Date(year, month - 1, day);
          const formattedDate = format(dateObj, "d 'de' MMMM", { locale: ptBR });

          return {
            dateKey,
            formattedDate,
            orders: groups[dateKey],
          };
        });
    },
  },

  actions: {
    clearForSessionChange() {
      this.requestGeneration++;
      this.stopHistoryPolling();
      this.stopTrackingPolling();
      this.orders = [];
      this.currentOrder = null;
      this.loading = false;
      this.detailLoading = false;
      this.actionLoading = false;
      this.error = null;
    },

    async fetchOrders(silent = false) {
      const generation = this.requestGeneration;
      if (!silent) {
        this.loading = true;
        this.orders = [];
      }
      this.error = null;

      try {
        const response = await customerOrdersApi.list();
        if (generation !== this.requestGeneration) return;
        this.orders = response;
      } catch (err: unknown) {
        if (generation !== this.requestGeneration) return;
        this.error = extractErrorMessage(err, "Erro ao carregar pedidos");
      } finally {
        if (!silent && generation === this.requestGeneration) {
          this.loading = false;
        }
      }
    },

    async fetchOrderById(orderId: string, silent = false) {
      const generation = this.requestGeneration;
      if (!silent) {
        this.detailLoading = true;
        this.currentOrder = null;
      }
      this.error = null;

      try {
        const response = await customerOrdersApi.getById(orderId);
        if (generation !== this.requestGeneration) return;
        this.currentOrder = response;
      } catch (err: unknown) {
        if (generation !== this.requestGeneration) return;
        this.error = extractErrorMessage(err, "Erro ao carregar pedido");
        this.currentOrder = null;
      } finally {
        if (!silent && generation === this.requestGeneration) {
          this.detailLoading = false;
        }
      }
    },

    async cancelOrder(orderId: string): Promise<boolean> {
      const generation = this.requestGeneration;
      this.actionLoading = true;
      this.error = null;

      try {
        const response = await customerOrdersApi.cancel(orderId);
        if (generation !== this.requestGeneration) return false;
        this.currentOrder = response;
        await this.fetchOrders(true);

        return true;
      } catch (err: unknown) {
        if (generation !== this.requestGeneration) return false;
        this.error = extractErrorMessage(err, "Erro ao cancelar pedido");

        return false;
      } finally {
        if (generation === this.requestGeneration) this.actionLoading = false;
      }
    },

    async createComplaint(orderId: string, message: string): Promise<boolean> {
      const generation = this.requestGeneration;
      this.actionLoading = true;
      this.error = null;

      try {
        const response = await customerOrdersApi.createComplaint(orderId, { message });
        if (generation !== this.requestGeneration) return false;
        this.currentOrder = response;
        await this.fetchOrders(true);

        return true;
      } catch (err: unknown) {
        if (generation !== this.requestGeneration) return false;
        this.error = extractErrorMessage(err, "Erro ao contestar pedido");

        return false;
      } finally {
        if (generation === this.requestGeneration) this.actionLoading = false;
      }
    },

    async closeComplaint(orderId: string, isComplaintSolved: boolean): Promise<boolean> {
      const generation = this.requestGeneration;
      this.actionLoading = true;
      this.error = null;

      try {
        const response = await customerOrdersApi.closeComplaint(orderId, { isComplaintSolved });
        if (generation !== this.requestGeneration) return false;
        this.currentOrder = response;
        await this.fetchOrders(true);

        return true;
      } catch (err: unknown) {
        if (generation !== this.requestGeneration) return false;
        this.error = extractErrorMessage(err, "Erro ao responder contestação");

        return false;
      } finally {
        if (generation === this.requestGeneration) this.actionLoading = false;
      }
    },

    async createReview(orderId: string, stars: number, comment?: string): Promise<boolean> {
      const generation = this.requestGeneration;
      this.actionLoading = true;
      this.error = null;

      try {
        const response = await customerOrdersApi.createReview(orderId, { stars, comment });
        if (generation !== this.requestGeneration) return false;
        this.currentOrder = response;
        await this.fetchOrders(true);

        return true;
      } catch (err: unknown) {
        if (generation !== this.requestGeneration) return false;
        this.error = extractErrorMessage(err, "Erro ao enviar avaliação");

        return false;
      } finally {
        if (generation === this.requestGeneration) this.actionLoading = false;
      }
    },

    startHistoryPolling() {
      this.stopHistoryPolling();

      this.pollTimer = setInterval(() => {
        if (typeof document !== "undefined" && document.visibilityState === "visible") {
          if (this.hasActiveOrders) {
            this.fetchOrders(true);
          }
        }
      }, 20000);
    },

    stopHistoryPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    startTrackingPolling(orderId: string) {
      this.stopTrackingPolling();

      this.trackingPollTimer = setInterval(() => {
        if (typeof document !== "undefined" && document.visibilityState === "visible") {
          if (this.currentOrder && !this.currentOrder.isFinished) {
            this.fetchOrderById(orderId, true);
          }
        }
      }, 15000);
    },

    stopTrackingPolling() {
      if (this.trackingPollTimer) {
        clearInterval(this.trackingPollTimer);
        this.trackingPollTimer = null;
      }
    },
  },
});
